import type { ComponentType } from "react";
import { useT } from "@transifex/react";
import { Exercises } from "../../../enums/exercises";
import type { StudentResultExercise } from "../../../helpers/StudentResultModel";
import StudentResultAnswersConnectWords from "./exerciseTypes/StudentResultAnswersConnectWords";
import StudentResultAnswersFillGap from "./exerciseTypes/StudentResultAnswersFillGap";
import StudentResultAnswersStandard from "./exerciseTypes/StudentResultAnswersStandard";
import StudentResultAnswersText from "./exerciseTypes/StudentResultAnswersText";
import StudentResultAnswersWriteSentence from "./exerciseTypes/StudentResultAnswersWriteSentence";

/** Every per-type renderer takes only the exercise. */
export interface StudentResultAnswersProps {
    exercise: StudentResultExercise;
}

// FREE_TEXT and CREATE_TEXT display identically; verb training is absent and falls back to Standard.
const answersByExerciseKind = new Map<Exercises, ComponentType<StudentResultAnswersProps>>([
    [Exercises.STANDARD, StudentResultAnswersStandard],
    [Exercises.FILL_GAP, StudentResultAnswersFillGap],
    [Exercises.CONNECT_WORDS, StudentResultAnswersConnectWords],
    [Exercises.WRITE_SENTENCE, StudentResultAnswersWriteSentence],
    [Exercises.FREE_TEXT, StudentResultAnswersText],
    [Exercises.CREATE_TEXT, StudentResultAnswersText],
]);

/**
 * Drill-in slot: the student's answers in the exercise's own layout. HALLOFUSS-6675 replaces each
 * renderer with its designed component without touching this dispatch.
 */
const StudentResultExerciseContent = (props: StudentResultAnswersProps) => {
    const t = useT();
    const t_noAnswersSubmitted = t("No answers submitted.", { _tags: "StudentResult, ExerciseContent" });

    if (props.exercise.answers.length === 0) {
        return <p data-testid="student-result-exercise-content">{t_noAnswersSubmitted}</p>;
    }

    const Answers =
        (props.exercise.exerciseKind && answersByExerciseKind.get(props.exercise.exerciseKind)) ||
        StudentResultAnswersStandard;

    return (
        <div data-testid="student-result-exercise-content">
            <Answers exercise={props.exercise} />
        </div>
    );
};

export default StudentResultExerciseContent;
